import { ButtonPrimary } from "components/Atoms/Button";
import { Panel } from "components/panels";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import ReadonlyArticle from "./ReadonlyArticle";
import { uploadFileToCoreApi } from "utilities/fileUploadUtils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LanguagesMenu from "./LanguagesMenu";
import EditArticle from "./EditArticle";
import BlogHeader from "./BlogHeader";
import { useLoaderData } from "react-router";

type States = NewState | EditState | ReadOnlyState

type NewState = { state: "newTranslation", language: server.dto.Languages }
type EditState = { state: "editTranslation", translation: server.dto.ArticleTranslation }
type ReadOnlyState = { state: "readOnly", translation: server.dto.ArticleTranslation }


const ArticleDetail = () => {
  const viewModel = useLoaderData<server.viewmodel.ArticleDetailFrontModel>();

  const articleApi = useQuery({
    queryKey: ["article", viewModel.article.articleId],
    queryFn: () => window.CoreApi.Article.GetArticle(viewModel.article.articleId).then(r => r.data.data),
    initialData: viewModel.article
  });

  const queryClient = useQueryClient();
  const resetQuery = () => queryClient.invalidateQueries(["article", viewModel.article.articleId]);

  const [editState, setEditState] = React.useState<States>(() => {
    if (articleApi.data.translations.length === 0) {
      return { state: "newTranslation", language: "english" }
    } else {
      return { state: "readOnly", translation: articleApi.data.translations.find(t => t.language === "english") ?? articleApi.data.translations[0] }
    }
  });
  const [needsSaving, setNeedsSaving] = React.useState(false);

  const onEditTranslationClicked = (translation: server.dto.ArticleTranslation) => {
    setEditState({ state: "editTranslation", translation })
  }

  const onDeleteTranslationClicked = (translation: server.dto.ArticleTranslation) => {
    window.displayDeleteConfirmationModal({
      onDeleteHandler: async () => {
        await window.CoreApi.Article.DeleteTranslation({ articleId: articleApi.data.articleId, language: translation.language });
        resetQuery();
        setEditState({ state: "readOnly", translation: articleApi.data.translations.find(t => t.language === "english") });
      }
    })
  }

  const onListImageImageUpload = async (file: File) => {
    const uploadRes = await createNewImage(file);
    if (typeof uploadRes === "string") {
      window.displayGenericModal("Error", uploadRes, "mini");
      return;
    }

    await window.CoreApi.Article.SetListImage(articleApi.data.articleId, uploadRes.fileId);
    resetQuery();
  }

  const createNewImage = async (file: File) => {
    const res = await uploadFileToCoreApi(
      window.CoreApi.Article.UploadArticleImage,
      window.CoreApi.Article.UploadArticleImage.absoluteUrl(),
      { file },
      { fileType: "image" }
    );
    if (res.data.hasErrors) {
      return res.data.errors[0].message;
    }

    return res.data.data;
  }

  const onLanguageSelected = (newLanguage: server.dto.Languages) => {
    if (needsSaving) {
      window.displayGenericModal("Warning", "You have unsaved changes. Please save your changes before switching language", "mini");
      return
    }

    const existingTranslation = articleApi.data.translations.find(t => t.language === newLanguage);
    if (existingTranslation) {
      setEditState({ state: "readOnly", translation: existingTranslation });
    } else {
      setEditState({ state: "newTranslation", language: newLanguage });
    }
    setNeedsSaving(false);
  }

  const isReadOnlyState = (state: States): state is ReadOnlyState => state.state === "readOnly";
  const isEditState = (state: States): state is EditState => state.state === "editTranslation";
  const isNewState = (state: States): state is NewState => state.state === "newTranslation";

  const enableSavingReminder = () => {
    setNeedsSaving(true);
  }

  const onCancel = () => {
    setNeedsSaving(false);
    if (isEditState(editState)) {
      setEditState({ state: "readOnly", translation: editState.translation });
    } else if (isNewState(editState)) {
      setEditState({ state: "readOnly", translation: articleApi.data.translations.find(t => t.language === "english") });
    }
  }

  const onFinishedSaving = async () => {
    setNeedsSaving(false);
    const res = await articleApi.refetch();

    if (isEditState(editState)) {
      setEditState({ state: "readOnly", translation: res.data.translations.find(t => t.language === editState.translation.language) });
    } else if (isNewState(editState)) {
      setEditState({ state: "readOnly", translation: res.data.translations.find(t => t.language === editState.language) });
    }
  }


  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <Panel panelClassName="s1_listview-panel" contentCssClass="flex-column gap" title={viewModel.pageTitle} headerSize="h1">
        {articleApi.data.section === "blog" && <BlogHeader article={articleApi.data} onUpdated={() => resetQuery()} />}

        <div className="flex">
          <div className="grow1">
            <ButtonPrimary label={"Back"} onClick={() => window.location.href = viewModel.articlesUrl} />
          </div>
          <div>
            <LanguagesMenu
              article={articleApi.data}
              allLanguages={viewModel.availableLanguages}
              selectedLanguage={isNewState(editState) ? editState.language : editState.translation.language}
              onLanguageClicked={onLanguageSelected}
            />
          </div>
        </div>

        {isReadOnlyState(editState) && <ReadonlyArticle article={articleApi.data} translation={editState.translation} onDelete={onDeleteTranslationClicked} onEdit={onEditTranslationClicked} onImageUpload={onListImageImageUpload} />}
        {isEditState(editState) && <EditArticle key={editState.translation.articleId} article={articleApi.data} existingTranslation={editState.translation} needsSaving={enableSavingReminder} onCancel={onCancel} onFinishedSaving={onFinishedSaving} language={editState.translation.language} />}
        {isNewState(editState) && <EditArticle key={editState.language} article={articleApi.data} existingTranslation={null} needsSaving={enableSavingReminder} onCancel={onCancel} onFinishedSaving={onFinishedSaving} language={editState.language} />}

      </Panel>
    </TwoColumnLayout.LeftColumn>
  </TwoColumnLayout>
}

export default ArticleDetail;